import '@fleetmatics/ui.insights/initialize';
import * as i3 from '@angular/common';
import { isPlatformBrowser, CommonModule, DOCUMENT } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, PLATFORM_ID, Injectable, Inject, NgModule, ErrorHandler, Component, Directive, HostListener, Renderer2, Optional, Input } from '@angular/core';
import * as i1$1 from '@ngrx/effects';
import { createEffect, ofType, EffectsModule } from '@ngrx/effects';
import * as i2 from '@ngrx/store';
import { createSelector, select, StoreModule } from '@ngrx/store';
import * as i1 from '@angular/common/http';
import { of } from 'rxjs';
import { withLatestFrom, mergeMap, map, filter } from 'rxjs/operators';
import * as i2$1 from '@angular/material/legacy-dialog';
import { MAT_LEGACY_DIALOG_DATA, MatLegacyDialogModule } from '@angular/material/legacy-dialog';
import * as i1$2 from '@angular/platform-browser';
import * as i4 from '@angular/material/icon';
import { MatIconModule } from '@angular/material/icon';
import * as i1$3 from '@fleetmatics/ui.utilities';

/*
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */
const ACCOUNT_SETTINGS_CONFIG = new InjectionToken('accountSettingsConfig');

/*
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */
const userGeneralSettingsUrl = '/v1/user/general-settings';
const accountSettingsUrl = '/v1/account';
class AccountSettingsService {
    constructor(http, config, _platformId) {
        this.http = http;
        this.config = config;
        this._platformId = _platformId;
    }
    getUserGeneralSettings() {
        return isPlatformBrowser(this._platformId)
            ? this.http.get(`${this.config.accountSettings.accountApiEndpoint}${userGeneralSettingsUrl}`)
            : of({ Data: { RowGuid: '', EmailAddress: '' } });
    }
    getAccountSettings() {
        return isPlatformBrowser(this._platformId)
            ? this.http.get(`${this.config.accountSettings.accountApiEndpoint}${accountSettingsUrl}`)
            : of({
                Data: {
                    AccountId: 0,
                    LanguageCode: '',
                    BusinessEmailAddress: '',
                    RegionId: 0,
                    UniversalAccountId: '',
                    SpeedLimitOption: 0 /* SpeedLimitOptionType.None */,
                    AccountName: '',
                    Currency: '',
                    City: '',
                    State: '',
                    ZipCode: '',
                    FormattedAddress: '',
                },
            });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccountSettingsService, deps: [{ token: i1.HttpClient }, { token: ACCOUNT_SETTINGS_CONFIG }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccountSettingsService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccountSettingsService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ACCOUNT_SETTINGS_CONFIG]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; } });

/*
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */
var AccountSettingsActionTypes;
(function (AccountSettingsActionTypes) {
    AccountSettingsActionTypes["LoadUserGeneralSettingsUserGuid"] = "[AccountSettings] Load User General Settings UserGuid";
    AccountSettingsActionTypes["LoadUserGeneralSettingsUserGuidSuccess"] = "[AccountSettings] Load User General Settings UserGuid Success";
    AccountSettingsActionTypes["LoadAccountSettings"] = "[AccountSettings] Load Account Settings";
    AccountSettingsActionTypes["LoadAccountSettingsSuccess"] = "[AccountSettings] Load Account Settings Success";
    AccountSettingsActionTypes["LoadAccountAndUserSettings"] = "[AccountSettings] Load Account and User Settings";
    AccountSettingsActionTypes["Noop"] = "[AccountSettings] Noop";
})(AccountSettingsActionTypes || (AccountSettingsActionTypes = {}));
class LoadUserGeneralSettingsUserGuid {
    constructor() {
        this.type = AccountSettingsActionTypes.LoadUserGeneralSettingsUserGuid;
    }
}
class LoadUserGeneralSettingsUserGuidSuccess {
    constructor(userGuid, emailAddress) {
        this.userGuid = userGuid;
        this.emailAddress = emailAddress;
        this.type = AccountSettingsActionTypes.LoadUserGeneralSettingsUserGuidSuccess;
    }
}
class LoadAccountSettings {
    constructor() {
        this.type = AccountSettingsActionTypes.LoadAccountSettings;
    }
}
class LoadAccountSettingsSuccess {
    constructor(accountSettings) {
        this.accountSettings = accountSettings;
        this.type = AccountSettingsActionTypes.LoadAccountSettingsSuccess;
    }
}
class LoadAccountAndUserSettings {
    constructor() {
        this.type = AccountSettingsActionTypes.LoadAccountAndUserSettings;
    }
}
class Noop {
    constructor() {
        this.type = AccountSettingsActionTypes.Noop;
    }
}

/*
 * Copyright © 2018-2019. Verizon Connect Ireland Limited. All rights reserved.
 */
const accountSettingsState = (state) => state.accountSettings;
const selectUserGeneralSettingsUserGuid = createSelector(accountSettingsState, state => state.userGuid);
const selectAccountSettingsAccountId = createSelector(accountSettingsState, state => state.accountId);
const selectAccountSettingsLang = createSelector(accountSettingsState, state => state.lang);
const selectAccountSettingsRegionId = createSelector(accountSettingsState, state => state.regionId);
const selectAccountSettingsUniversalId = createSelector(accountSettingsState, state => state.universalAccountId);
const selectAccountSettingsSpeedLimitOption = createSelector(accountSettingsState, state => state.speedLimitOption);
const selectUserGuidIsLoaded = createSelector(accountSettingsState, state => state.isUserGuidLoaded);
const selectAccountSettingsIsLoaded = createSelector(accountSettingsState, state => state.isAccountSettingsLoaded);
const selectAccountAndUser = createSelector(accountSettingsState, state => ({
    userGuid: state.userGuid,
    accountId: state.accountId,
    lang: state.lang,
    regionId: state.regionId,
    universalAccountId: state.universalAccountId,
    speedLimitOption: state.speedLimitOption,
    businessEmailAddress: state.businessEmailAddress,
    city: state.city,
    state: state.state,
    zipCode: state.zipCode,
    formattedAddress: state.formattedAddress,
    accountName: state.accountName,
    currency: state.currency,
}));

/*
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */
class AccountSettingsEffects {
    constructor(actions$, store, service) {
        this.actions$ = actions$;
        this.store = store;
        this.service = service;
        this.loadUserGeneralSettingsUserGuid$ = createEffect(() => this.actions$.pipe(ofType(AccountSettingsActionTypes.LoadUserGeneralSettingsUserGuid, AccountSettingsActionTypes.LoadAccountAndUserSettings), withLatestFrom(this.store.pipe(select(selectUserGuidIsLoaded))), mergeMap(([_action, isLoaded]) => {
            if (!isLoaded) {
                return this.service
                    .getUserGeneralSettings()
                    .pipe(map(data => new LoadUserGeneralSettingsUserGuidSuccess(data.Data.RowGuid, data.Data.EmailAddress)));
            }
            else {
                return of(new Noop());
            }
        })));
        this.loadAccountSettings$ = createEffect(() => this.actions$.pipe(ofType(AccountSettingsActionTypes.LoadAccountSettings, AccountSettingsActionTypes.LoadAccountAndUserSettings), withLatestFrom(this.store.pipe(select(selectAccountSettingsIsLoaded))), mergeMap(([_action, isLoaded]) => {
            if (!isLoaded) {
                return this.service.getAccountSettings().pipe(map(data => new LoadAccountSettingsSuccess({
                    accountId: data.Data.AccountId,
                    regionId: data.Data.RegionId,
                    lang: data.Data.LanguageCode,
                    universalAccountId: data.Data.UniversalAccountId,
                    speedLimitOption: data.Data.SpeedLimitOption,
                    accountName: data.Data.AccountName,
                    city: data.Data.City,
                    state: data.Data.State,
                    zipCode: data.Data.ZipCode,
                    formattedAddress: data.Data.FormattedAddress,
                    currency: data.Data.Currency,
                })));
            }
            else {
                return of(new Noop());
            }
        })));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccountSettingsEffects, deps: [{ token: i1$1.Actions }, { token: i2.Store }, { token: AccountSettingsService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccountSettingsEffects }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccountSettingsEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.Actions }, { type: i2.Store }, { type: AccountSettingsService }]; } });

/*
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */
const accountSettingsInitialState = {
    isUserGuidLoaded: false,
    isAccountSettingsLoaded: false,
};

/*
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */
function accountSettingsReducer(state = accountSettingsInitialState, action) {
    switch (action.type) {
        case AccountSettingsActionTypes.LoadUserGeneralSettingsUserGuidSuccess:
            return { ...state, isUserGuidLoaded: true, userGuid: action.userGuid, businessEmailAddress: action.emailAddress };
        case AccountSettingsActionTypes.LoadAccountSettingsSuccess:
            return {
                ...state,
                isAccountSettingsLoaded: true,
                ...action.accountSettings
            };
        default:
            return state;
    }
}

/*
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */
class AccountSettingsModule {
    static forRoot(configs) {
        return {
            ngModule: AccountSettingsModule,
            providers: [
                { provide: ACCOUNT_SETTINGS_CONFIG, useValue: configs }
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccountSettingsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: AccountSettingsModule, imports: [CommonModule, i2.StoreFeatureModule, i1$1.EffectsFeatureModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccountSettingsModule, providers: [
            AccountSettingsService
        ], imports: [CommonModule,
            StoreModule.forFeature('accountSettings', accountSettingsReducer),
            EffectsModule.forFeature([AccountSettingsEffects])] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AccountSettingsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        StoreModule.forFeature('accountSettings', accountSettingsReducer),
                        EffectsModule.forFeature([AccountSettingsEffects])
                    ],
                    providers: [
                        AccountSettingsService
                    ]
                }]
        }] });

/*!
 * Copyright © 2019. Verizon Connect Ireland Limited. All rights reserved.
 */

/*
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */

/*
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */

/*
 * Copyright © 2018-2019. Verizon Connect Ireland Limited. All rights reserved.
 */

/*
 * Copyright © 2019. Verizon Connect Ireland Limited. All rights reserved.
 */
const APP_DYNAMICS_SCRIPT_VERSION = '20.12.0.3360';
/**
 * Template Script for appending AppDynamics configuration from Express web server.
 */
const appDynamicsScriptConfigTemplate = (appKey, beaconDomain) => `<script charset='UTF-8'>
window['adrum-start-time'] = new Date().getTime();
(function(config){
    config.appKey = '${appKey}';
    config.adrumExtUrlHttp = 'http://cdn.appdynamics.com';
    config.adrumExtUrlHttps = 'https://cdn.appdynamics.com';
    config.beaconUrlHttp = 'http://${beaconDomain}';
    config.beaconUrlHttps = 'https://${beaconDomain}';
    config.xd = {enable: true};
    config.spa = {spa2: true};
    config.fetch = false;
    config.isZonePromise = true;
})(window['adrum-config'] || (window['adrum-config'] = {}));
</script>`;
/**
 * Template for appending AppDynamics script from Express web server.
 */
const appDynamicsLoadScriptTemplate = (version) => `<script src='//cdn.appdynamics.com/adrum/adrum-${version}.js'></script>`;
/**
 * Inserts the AppDynamics script to a HTML Document.
 *
 * To be used AFTER Server Side Rendering.
 */
const insertAppDynamicsScript = (html, isAppDynamicsEnabled, appKey, beaconDomain, version = APP_DYNAMICS_SCRIPT_VERSION, loadScriptAfterBundles = false) => {
    if (!isAppDynamicsEnabled) {
        return html;
    }
    if (loadScriptAfterBundles) {
        return html
            .replace('<head>', '<head>' + appDynamicsScriptConfigTemplate(appKey, beaconDomain))
            .replace('</body>', appDynamicsLoadScriptTemplate(version) + '</body>');
    }
    return html.replace('<head>', '<head>' + appDynamicsScriptConfigTemplate(appKey, beaconDomain) + appDynamicsLoadScriptTemplate(version));
};
/**
 * @deprecated use insertAppDynamicsScript instead.
 */
const appendAppDynamicsScript = insertAppDynamicsScript;
const staticAppDynamicsScriptConfigTemplate = (appKeyToken, beaconDomainToken, isEnabledToken) => `<script charset='UTF-8'>
  (function() {
    if (!/(true|1)/i.test('${isEnabledToken}')) return;
    window['adrum-start-time'] = new Date().getTime();
    (function(config){
      config.appKey = '${appKeyToken}';
      config.adrumExtUrlHttp = 'http://cdn.appdynamics.com';
      config.adrumExtUrlHttps = 'https://cdn.appdynamics.com';
      config.beaconUrlHttp = 'http://${beaconDomainToken}';
      config.beaconUrlHttps = 'https://${beaconDomainToken}';
      config.xd = {enable: true};
      config.spa = {spa2: true};
      config.fetch = false;
      config.isZonePromise = true;
    })(window['adrum-config'] || (window['adrum-config'] = {}));
  }())
  </script>`;
/**
 * Inserts the AppDynamics script to a HTML Document.
 *
 * To be used in post build process.
 *
 * This differs from insertAppDynamicsScript(..) in that this
 * runs inserts as part of the post build process. All arguments
 * except `html` are passed as string tokens to be replaced in the
 * index.html at deploy time by Octopus.
 */
const staticInsertAppDynamicsScript = (html, isEnabledToken, appKeyToken, beaconDomainToken, versionToken, loadScriptAfterBundles = false) => {
    if (loadScriptAfterBundles) {
        return html
            .replace('<head>', '<head>' + staticAppDynamicsScriptConfigTemplate(appKeyToken, beaconDomainToken, isEnabledToken))
            .replace('</body>', appDynamicsLoadScriptTemplate(versionToken) + '</body>');
    }
    return html.replace('<head>', '<head>' +
        staticAppDynamicsScriptConfigTemplate(appKeyToken, beaconDomainToken, isEnabledToken) +
        appDynamicsLoadScriptTemplate(versionToken));
};

/*
 * Copyright © 2019. Verizon Connect Ireland Limited. All rights reserved.
 */
const ORIGINAL_ERROR_HANDLER = new InjectionToken('originalErrorHandler');
class AppDynamicsErrorHandler {
    constructor(originalErrorHandler) {
        this.originalErrorHandler = originalErrorHandler;
        this.adrum = typeof ADRUM === 'undefined' ? undefined : ADRUM;
    }
    handleError(error) {
        if (this.adrum) {
            const customError = new this.adrum.events.Error();
            customError.msg(`${error.message} ${error.stack}`);
            this.adrum.report(customError);
        }
        this.originalErrorHandler.handleError(error);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AppDynamicsErrorHandler, deps: [{ token: ORIGINAL_ERROR_HANDLER }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AppDynamicsErrorHandler }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AppDynamicsErrorHandler, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i0.ErrorHandler, decorators: [{
                    type: Inject,
                    args: [ORIGINAL_ERROR_HANDLER]
                }] }]; } });

/*
 * Copyright © 2019. Verizon Connect Ireland Limited. All rights reserved.
 */
class AppDynamicsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AppDynamicsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: AppDynamicsModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AppDynamicsModule, providers: [
            { provide: ErrorHandler, useClass: AppDynamicsErrorHandler },
            { provide: ORIGINAL_ERROR_HANDLER, useClass: ErrorHandler }
        ] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AppDynamicsModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [
                        { provide: ErrorHandler, useClass: AppDynamicsErrorHandler },
                        { provide: ORIGINAL_ERROR_HANDLER, useClass: ErrorHandler }
                    ]
                }]
        }] });

/*
 * Copyright © 2019. Verizon Connect Ireland Limited. All rights reserved.
 */

/*!
 * Copyright © 2020. Verizon Connect Ireland Limited. All rights reserved.
 */
const NPS_SURVEY_CONFIG = new InjectionToken('npsSurveyConfig');

/*!
 * Copyright © 2020. Verizon Connect Ireland Limited. All rights reserved.
 */
class NpsSurveyService {
    constructor(http, config, _platformId) {
        this.http = http;
        this.config = config;
        this._platformId = _platformId;
    }
    showNpsSurvey() {
        return isPlatformBrowser(this._platformId)
            ? this.http.get(`${this.config.npsSurvey.npsSurveyApiEndpoint}/ShowNpsSurvey`).pipe(map((response) => {
                return {
                    userGuid: response.Data.UserGuid,
                    surveyUrl: response.Data.SurveyUrl,
                    isValidSurvey: response.Data.IsValidSurvey,
                    surveyId: response.Data.SurveyId,
                };
            }))
            : of({ userGuid: '', surveyUrl: '', isValidSurvey: false, surveyId: 0 });
    }
    updateNpsSurvey(surveyId, completed) {
        return isPlatformBrowser(this._platformId)
            ? this.http
                .post(`${this.config.npsSurvey.npsSurveyApiEndpoint}/UpdateNpsSurvey`, { SurveyId: surveyId, Completed: completed })
                .pipe(map((response) => response.Data))
            : of(false);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NpsSurveyService, deps: [{ token: i1.HttpClient }, { token: NPS_SURVEY_CONFIG }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NpsSurveyService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NpsSurveyService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [NPS_SURVEY_CONFIG]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; } });

/*!
 * Copyright © 2020. Verizon Connect Ireland Limited. All rights reserved.
 */
class NpsSurveyDialogComponent {
    constructor(sanitizer, dialogRef, data) {
        this.sanitizer = sanitizer;
        this.dialogRef = dialogRef;
        this.surveyUrl = null;
        this.surveyUrl = this.sanitizer.bypassSecurityTrustResourceUrl(data.surveyUrl);
    }
    closeMe() {
        this.dialogRef.close();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NpsSurveyDialogComponent, deps: [{ token: i1$2.DomSanitizer }, { token: i2$1.MatLegacyDialogRef }, { token: MAT_LEGACY_DIALOG_DATA }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: NpsSurveyDialogComponent, selector: "fm-nps-survey-dialog", ngImport: i0, template: "<div id=\"nps-dialog\" class=\"nps-dialog\" a7s-id=\"nps-survey-microsite\">\n  <div class=\"nps-close-bar\">\n    <mat-icon (click)=\"closeMe()\" class=\"nps-close-icon\" svgIcon=\"ds-ic-close\"></mat-icon>\n  </div>\n\n  <iframe *ngIf=\"surveyUrl\" [src]=\"surveyUrl\"></iframe>\n</div>\n", styles: [".nps-dialog{display:flex;flex-direction:column;align-items:stretch;height:100%}.nps-close-bar{display:flex;flex-direction:row;justify-content:flex-end;margin-bottom:8px}.nps-close-icon{width:16px;height:16px;flex:none;margin-top:-8px;cursor:pointer}iframe{flex:1 1 0px;width:100%}\n"], dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NpsSurveyDialogComponent, decorators: [{
            type: Component,
            args: [{ selector: 'fm-nps-survey-dialog', template: "<div id=\"nps-dialog\" class=\"nps-dialog\" a7s-id=\"nps-survey-microsite\">\n  <div class=\"nps-close-bar\">\n    <mat-icon (click)=\"closeMe()\" class=\"nps-close-icon\" svgIcon=\"ds-ic-close\"></mat-icon>\n  </div>\n\n  <iframe *ngIf=\"surveyUrl\" [src]=\"surveyUrl\"></iframe>\n</div>\n", styles: [".nps-dialog{display:flex;flex-direction:column;align-items:stretch;height:100%}.nps-close-bar{display:flex;flex-direction:row;justify-content:flex-end;margin-bottom:8px}.nps-close-icon{width:16px;height:16px;flex:none;margin-top:-8px;cursor:pointer}iframe{flex:1 1 0px;width:100%}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$2.DomSanitizer }, { type: i2$1.MatLegacyDialogRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_LEGACY_DIALOG_DATA]
                }] }]; } });

/*!
 * Copyright © 2020. Verizon Connect Ireland Limited. All rights reserved.
 */
class NpsSurveyDirective {
    constructor(npsSurveyService, dialog, platformId, document, config) {
        this.npsSurveyService = npsSurveyService;
        this.dialog = dialog;
        this.platformId = platformId;
        this.document = document;
        this.config = config;
        this.surveySubmitted = false;
    }
    ngOnInit() {
        if (this.skipSurvey()) {
            return;
        }
        this.npsSurveyService
            .showNpsSurvey()
            .pipe(filter(s => !!s && !!s.surveyUrl && !!s.userGuid && s.isValidSurvey))
            .subscribe(s => this.showSurvey(s), error => console.log('error at NpsSurveyService', error));
    }
    showSurvey(survey) {
        survey.surveyUrl = survey.surveyUrl.replace('{0}', survey.userGuid);
        this.survey = survey;
        this.dialogRef = this.dialog.open(NpsSurveyDialogComponent, {
            width: '568px',
            height: '564px',
            data: survey,
            disableClose: true
        });
        this.dialogRef.afterClosed().subscribe(_ => this.onSurveyDialogClosed(survey));
    }
    onSurveyCompletedMessageReceived(event) {
        if (!event || !event.data) {
            return;
        }
        const expectedCompletionMessage = 'surveyCompleted';
        if (expectedCompletionMessage !== event.data) {
            return;
        }
        this.surveySubmitted = true;
        this.closeDialog();
    }
    onSurveyDialogClosed(survey) {
        this.npsSurveyService.updateNpsSurvey(survey.surveyId, this.surveySubmitted)
            .subscribe();
    }
    closeDialog() {
        if (!this.dialogRef) {
            return;
        }
        this.dialogRef.close();
    }
    skipSurvey() {
        if (!this.config.npsSurvey.enabled) {
            return true;
        }
        if (!isPlatformBrowser(this.platformId)) {
            return true;
        }
        const referrerUrl = this.document.referrer.toLowerCase();
        return !referrerUrl.includes('closeandredirect.html') && !referrerUrl.includes('login.aspx');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NpsSurveyDirective, deps: [{ token: NpsSurveyService }, { token: i2$1.MatLegacyDialog }, { token: PLATFORM_ID }, { token: DOCUMENT }, { token: NPS_SURVEY_CONFIG }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: NpsSurveyDirective, selector: "fm-nps-survey", host: { listeners: { "window:message": "onSurveyCompletedMessageReceived($event)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NpsSurveyDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'fm-nps-survey'
                }]
        }], ctorParameters: function () { return [{ type: NpsSurveyService }, { type: i2$1.MatLegacyDialog }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [NPS_SURVEY_CONFIG]
                }] }]; }, propDecorators: { onSurveyCompletedMessageReceived: [{
                type: HostListener,
                args: ['window:message', ['$event']]
            }] } });

/*!
 * Copyright © 2020. Verizon Connect Ireland Limited. All rights reserved.
 */

/*
 * Copyright © 2020. Verizon Connect Ireland Limited. All rights reserved.
 */
class NpsSurveyModule {
    static forRoot(configs) {
        return {
            ngModule: NpsSurveyModule,
            providers: [
                NpsSurveyService,
                { provide: NPS_SURVEY_CONFIG, useValue: configs },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NpsSurveyModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: NpsSurveyModule, declarations: [NpsSurveyDirective, NpsSurveyDialogComponent], imports: [CommonModule, MatLegacyDialogModule, MatIconModule], exports: [NpsSurveyDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NpsSurveyModule, imports: [CommonModule, MatLegacyDialogModule, MatIconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NpsSurveyModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, MatLegacyDialogModule, MatIconModule],
                    declarations: [NpsSurveyDirective, NpsSurveyDialogComponent],
                    exports: [NpsSurveyDirective]
                }]
        }] });

/*
 * Copyright © 2019-2020. Verizon Connect Ireland Limited. All rights reserved.
 */

/*
 * Copyright © 2019-2022. Verizon Connect Ireland Limited. All rights reserved.
 */
const getApiKey = (system) => {
    switch (system) {
        case 'Telogis':
            return 'fc8b7fd9-b651-4e2e-4060-4e5113c1cf81';
        default:
            return '8afa59c8-1249-40a0-7e75-b7e82bcc2e72'; // default is always the Reveal Key
    }
};
const pendoScriptTemplate = (values, system) => `
(function(apiKey){
    (function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=[];
    v=['initialize','identify','updateOptions','pageLoad'];for(w=0,x=v.length;w<x;++w)(function(m){
        o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
        y=e.createElement(n);y.async=!0;y.src='https://cdn.pendo.io/agent/static/'+apiKey+'/pendo.js';
        z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');

        pendo.initialize(${JSON.stringify(values)});
})('${getApiKey(system)}');
`;

/*!
 * Copyright © 2020. Verizon Connect Ireland Limited. All rights reserved.
 */
const PENDO_CONFIG = new InjectionToken('pendoConfig');

/*!
 * Copyright © 2018-2022. Verizon Connect Ireland Limited. All rights reserved.
 */
const LOCALE_COOKIE_KEY = 'locale';
const FLEETCARE_ACCOUNTID = 'fleetcare';
const PENDO_TEMPLATE = new InjectionToken('pendoTemplate');
class PendoDirective {
    set systemId(systemId) {
        this._systemId = systemId;
        this.loadPendoScript();
    }
    set dvir(permission) {
        this._dvir = permission.permissionId === 295;
        this.loadPendoScript();
    }
    set userGuid(userGuid) {
        this._userGuid = userGuid;
        this.loadPendoScript();
    }
    set localeCookieKey(localeCookieKey) {
        this._localeCookieKey = localeCookieKey;
        this.loadPendoScript();
    }
    set accountId(accountId) {
        this._accountId = accountId;
        this.loadPendoScript();
    }
    set regionId(regionId) {
        this._regionId = regionId;
        this.loadPendoScript();
    }
    set lang(lang) {
        this._lang = lang;
        this.loadPendoScript();
    }
    /**
     * @deprecated
     *
     * We no longer pass this value to Pendo
     * This setter will be removed in the future
     */
    set businessEmailAddress(businessEmailAddress) {
        this._businessEmailAddress = businessEmailAddress;
        this.loadPendoScript();
    }
    set universalAccountId(universalAccountId) {
        this._universalAccountId = universalAccountId;
        this.loadPendoScript();
    }
    set accountAndUser(settings) {
        if (!settings) {
            return;
        }
        this._userGuid = settings.userGuid || this._userGuid;
        this._accountId = settings.accountId || this._accountId;
        this._lang = settings.lang || this._lang;
        this._businessEmailAddress = settings.businessEmailAddress || this._businessEmailAddress;
        this._regionId = settings.regionId || this._regionId;
        this._universalAccountId = settings.universalAccountId || this._universalAccountId;
        this._localeCookieKey = settings.localeCookieKey || LOCALE_COOKIE_KEY;
        this.loadPendoScript();
    }
    set accountUserPermission(settings) {
        if (!settings) {
            return;
        }
        this._userGuid = settings.userGuid || this._userGuid;
        this._accountId = settings.accountId || this._accountId;
        this._lang = settings.lang || this._lang;
        this._businessEmailAddress = settings.businessEmailAddress || this._businessEmailAddress;
        this._regionId = settings.regionId || this._regionId;
        this._universalAccountId = settings.universalAccountId || this._universalAccountId;
        this._localeCookieKey = settings.localeCookieKey || LOCALE_COOKIE_KEY;
        this._dvir = settings.dvirEnabled;
        this.loadPendoScript();
    }
    set fleetcareUser(fleetcareUserHash) {
        this._accountId = FLEETCARE_ACCOUNTID;
        this._userGuid = fleetcareUserHash;
        this.loadPendoScript();
    }
    set accountUserSystem(settings) {
        if (!settings) {
            return;
        }
        this._userGuid = settings.userGuid || this._userGuid;
        this._accountId = settings.accountId || this._accountId;
        this._lang = settings.lang || this._lang;
        this._regionId = settings.regionId || this._regionId;
        this._universalAccountId = settings.universalAccountId || this._universalAccountId;
        this._localeCookieKey = settings.localeCookieKey || LOCALE_COOKIE_KEY;
        this._systemId = settings.systemId;
        this.loadPendoScript();
    }
    set isAnonymousVisitor(isAnonymousVisitor) {
        if (!isAnonymousVisitor) {
            return;
        }
        this._isAnonymousVisitor = isAnonymousVisitor;
        this.loadPendoScript();
    }
    constructor(doc, renderer, platformId, cookie, pendoTemplate, pendoConfig) {
        this.doc = doc;
        this.renderer = renderer;
        this.platformId = platformId;
        this.cookie = cookie;
        this.pendoTemplate = pendoTemplate;
        this._scriptAlreadyLoaded = false;
        this._systemId = 'Reveal';
        // simple IoC to allow this to be replaced for tests
        if (typeof this.pendoTemplate !== 'function') {
            this.pendoTemplate = pendoScriptTemplate;
        }
        this._enabled = pendoConfig && pendoConfig.pendo && pendoConfig.pendo.enabled;
        if (this._enabled == null) {
            // will match null or undefined but not false
            this._enabled = true;
        }
        this._prefix = pendoConfig?.pendo?.prefix ?? '';
    }
    canLoadScript() {
        return this._enabled && isPlatformBrowser(this.platformId) && !this._scriptAlreadyLoaded;
    }
    areRevealFieldsFilled() {
        return !!(this._localeCookieKey && this._userGuid && this._accountId && this._regionId && this._lang && this._universalAccountId);
    }
    areFleetcareFieldsFilled() {
        return !!(this._accountId === FLEETCARE_ACCOUNTID && this._userGuid);
    }
    loadPendoScript() {
        if (!this.canLoadScript()) {
            return;
        }
        if (!(this._isAnonymousVisitor || this.areRevealFieldsFilled() || this.areFleetcareFieldsFilled())) {
            return;
        }
        const script = this.renderer.createElement('script');
        script.id = 'vzc-pendo';
        const accountIdWithPrefix = this._prefix && this._prefix !== 'us-prod' && this._prefix !== 'eu-prod'
            ? `${this._prefix}-${this._accountId}`
            : `${this._accountId}`;
        script.innerHTML =
            this._isAnonymousVisitor === true
                ? this.pendoTemplate({}, this._systemId)
                : this.pendoTemplate({
                    visitor: {
                        id: this._userGuid,
                        lang: this.cookie.getCookie(this._localeCookieKey) ?? undefined,
                    },
                    account: {
                        id: accountIdWithPrefix,
                        regionId: this._regionId,
                        lang: this._lang,
                        universalAccountId: this._universalAccountId,
                        ...(this._dvir && { dvirEnabled: this._dvir }),
                    },
                }, this._systemId);
        this.renderer.appendChild(this.doc.body, script);
        this._scriptAlreadyLoaded = true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PendoDirective, deps: [{ token: DOCUMENT }, { token: Renderer2 }, { token: PLATFORM_ID }, { token: i1$3.FmCookieService }, { token: PENDO_TEMPLATE, optional: true }, { token: PENDO_CONFIG, optional: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: PendoDirective, selector: "fm-pendo", inputs: { systemId: "systemId", dvir: "dvir", userGuid: "userGuid", localeCookieKey: "localeCookieKey", accountId: "accountId", regionId: "regionId", lang: "lang", businessEmailAddress: "businessEmailAddress", universalAccountId: "universalAccountId", accountAndUser: "accountAndUser", accountUserPermission: "accountUserPermission", fleetcareUser: "fleetcareUser", accountUserSystem: "accountUserSystem", isAnonymousVisitor: "isAnonymousVisitor" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PendoDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'fm-pendo',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i0.Renderer2, decorators: [{
                    type: Inject,
                    args: [Renderer2]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: i1$3.FmCookieService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PENDO_TEMPLATE]
                }, {
                    type: Optional
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [PENDO_CONFIG]
                }] }]; }, propDecorators: { systemId: [{
                type: Input
            }], dvir: [{
                type: Input
            }], userGuid: [{
                type: Input
            }], localeCookieKey: [{
                type: Input
            }], accountId: [{
                type: Input
            }], regionId: [{
                type: Input
            }], lang: [{
                type: Input
            }], businessEmailAddress: [{
                type: Input
            }], universalAccountId: [{
                type: Input
            }], accountAndUser: [{
                type: Input
            }], accountUserPermission: [{
                type: Input
            }], fleetcareUser: [{
                type: Input
            }], accountUserSystem: [{
                type: Input
            }], isAnonymousVisitor: [{
                type: Input
            }] } });

/*!
 * Copyright © 2020. Verizon Connect Ireland Limited. All rights reserved.
 */
class PendoService {
    constructor(document, platformId) {
        this.document = document;
        this.platformId = platformId;
    }
    trackEvent(eventName, properties) {
        const window = this.document.defaultView;
        if (window && this._isPendoReady(window)) {
            window.pendo.track(eventName, properties);
        }
    }
    _isPendoReady(window) {
        return Boolean(isPlatformBrowser(this.platformId) && window.pendo?.track && window.pendo?.isReady?.());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PendoService, deps: [{ token: DOCUMENT }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PendoService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PendoService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; } });

/*
 * Copyright © 2018-2020. Verizon Connect Ireland Limited. All rights reserved.
 */
class PendoModule {
    static forRoot(configs) {
        return {
            ngModule: PendoModule,
            providers: [
                PendoService,
                { provide: PENDO_CONFIG, useValue: configs }
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PendoModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: PendoModule, declarations: [PendoDirective], imports: [CommonModule], exports: [PendoDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PendoModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PendoModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [PendoDirective],
                    exports: [PendoDirective]
                }]
        }] });

/*
 * Copyright © 2018-2020. Verizon Connect Ireland Limited. All rights reserved.
 */

/*
 * Copyright © 2018-2020. Verizon Connect Ireland Limited. All rights reserved.
 */

/*
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */
/*
 * Public API Surface of insights-lib
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ACCOUNT_SETTINGS_CONFIG, AccountSettingsActionTypes, AccountSettingsModule, AppDynamicsErrorHandler, AppDynamicsModule, LoadAccountAndUserSettings, LoadAccountSettings, LoadAccountSettingsSuccess, LoadUserGeneralSettingsUserGuid, LoadUserGeneralSettingsUserGuidSuccess, NPS_SURVEY_CONFIG, Noop, NpsSurveyDirective, NpsSurveyModule, ORIGINAL_ERROR_HANDLER, PENDO_CONFIG, PENDO_TEMPLATE, PendoDirective, PendoModule, PendoService, accountSettingsReducer, accountSettingsState, appDynamicsLoadScriptTemplate, appDynamicsScriptConfigTemplate, appendAppDynamicsScript, insertAppDynamicsScript, selectAccountAndUser, selectAccountSettingsAccountId, selectAccountSettingsIsLoaded, selectAccountSettingsLang, selectAccountSettingsRegionId, selectAccountSettingsSpeedLimitOption, selectAccountSettingsUniversalId, selectUserGeneralSettingsUserGuid, selectUserGuidIsLoaded, staticAppDynamicsScriptConfigTemplate, staticInsertAppDynamicsScript };
